import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail, tokenStore, stripTokens } from "@/lib/api";
import { disablePush } from "@/lib/push";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, object = authenticated, false = not authenticated
  const [user, setUser] = useState(null);
  const [previewMode, setPreviewModeState] = useState(
    () => typeof window !== "undefined" && window.localStorage.getItem("peeping_preview") === "1"
  );

  const togglePreview = () => {
    const next = !previewMode;
    window.localStorage.setItem("peeping_preview", next ? "1" : "0");
    if (next) api.defaults.headers.common["X-Peeping-Preview"] = "subscriber";
    else delete api.defaults.headers.common["X-Peeping-Preview"];
    setPreviewModeState(next);
    window.location.reload();
  };

  const refreshMe = useCallback(async (attempt = 0) => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (e) {
      // Offline / server restarting: keep the saved session and retry instead of logging out.
      const transient = !e.response || e.response.status >= 500;
      if (transient && tokenStore.refresh() && attempt < 20) {
        await new Promise((r) => setTimeout(r, Math.min(2000 * (attempt + 1), 10000)));
        return refreshMe(attempt + 1);
      }
      setUser(false);
      return false;
    }
  }, []);

  useEffect(() => {
    refreshMe();
    const expired = () => setUser(false);
    const onStorage = (e) => { if (e.key === "peeping_rt" && !e.newValue) setUser(false); };
    window.addEventListener("peeping-auth-expired", expired);
    window.addEventListener("storage", onStorage);
    return () => { window.removeEventListener("peeping-auth-expired", expired); window.removeEventListener("storage", onStorage); };
  }, [refreshMe]);

  // Accepts any auth response (login/register/setup/invite), stores the Bearer fallback.
  const startSession = useCallback((data) => {
    tokenStore.save(data);
    const u = stripTokens(data);
    setUser(u);
    return u;
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    return startSession(data);
  };

  const register = async (username, email, password) => {
    const { data } = await api.post("/auth/register", { username, email, password });
    return startSession(data);
  };

  const logout = async () => {
    await disablePush().catch(() => {});
    try {
      await api.post("/auth/logout", { refresh_token: tokenStore.refresh() });
    } catch {
      /* ignore */
    }
    tokenStore.clear();
    window.localStorage.removeItem("peeping_preview");
    delete api.defaults.headers.common["X-Peeping-Preview"];
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, startSession, login, register, logout, refreshMe, formatApiErrorDetail, previewMode, togglePreview }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
